import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Calendar as CalendarIcon, LogIn, LogOut, User } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import './Navbar.css';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleLogin = () => {
    navigate('/?login=true');
  };

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/events', label: 'Events' },
    { path: '/products', label: 'Products' },
    { path: '/artists', label: 'Artists' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/venue-portfolio', label: 'Venue Portfolio' },
    { path: '/about', label: 'About' },
    { path: '/subscribe', label: 'Subscribe' }
  ];

  return (
    <nav className={`navbar ${scrolled ? 'navbar-scrolled' : ''}`}>
      <div className="container">
        <div className="navbar-content">
          {/* Logo */}
          <Link to="/" className="navbar-logo">
            <img 
              src="/Round Transparent black.png" 
              alt="Back to People" 
              className="navbar-logo-img"
            />
            <div className="navbar-logo-text">
              <span className="navbar-logo-b2p">B2P</span>
              <span className="navbar-logo-full">Back to People</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="navbar-links">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`navbar-link ${location.pathname === link.path ? 'active' : ''}`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Calendar Link (Protected) */}
            <Link
              to="/calendar"
              className={`navbar-link navbar-link-calendar ${location.pathname === '/calendar' ? 'active' : ''}`}
            >
              <CalendarIcon size={18} />
              Calendar
            </Link>

            {/* Auth Buttons */}
            {user ? (
              <div className="navbar-user">
                <Link
                  to="/profile"
                  className={`navbar-link ${location.pathname === '/profile' ? 'active' : ''}`}
                >
                  <User size={16} />
                  Profile
                </Link>
                <span className="navbar-user-name">
                  {user.name || user.firstName}
                </span>
                <button onClick={handleLogout} className="navbar-logout-btn">
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            ) : (
              <button onClick={handleLogin} className="navbar-login-btn">
                <LogIn size={16} />
                Login
              </button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="navbar-toggle"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="navbar-mobile">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`navbar-mobile-link ${location.pathname === link.path ? 'active' : ''}`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Calendar Link Mobile */}
            <Link
              to="/calendar"
              className={`navbar-mobile-link ${location.pathname === '/calendar' ? 'active' : ''}`}
            >
              <CalendarIcon size={18} />
              Calendar
            </Link>

            {/* Auth Mobile */}
            {user ? (
              <>
                <Link
                  to="/profile"
                  className={`navbar-mobile-link ${location.pathname === '/profile' ? 'active' : ''}`}
                >
                  <User size={18} />
                  Profile
                </Link>
                <div className="navbar-mobile-user">
                  {user.name || user.firstName}
                </div>
                <button onClick={handleLogout} className="navbar-mobile-link navbar-mobile-btn">
                  <LogOut size={18} />
                  Logout
                </button>
              </>
            ) : (
              <button onClick={handleLogin} className="navbar-mobile-link navbar-mobile-btn">
                <LogIn size={18} />
                Login
              </button>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;

